import { DateTime, DateTimeUnit } from 'luxon'

export type ParsedDateMath = Partial<Record<DateTimeUnit, number>>

const UNITS = [
	['y', 'year'],
	['M', 'month'],
	['w', 'week'],
	['d', 'day'],
	['h', 'hour'],
	['m', 'minute'],
	['s', 'second'],
] as const

const units = new Map<string, DateTimeUnit>(UNITS)
const unitsReversed = new Map<DateTimeUnit, string>(UNITS.map(([k, v]) => [v, k]))

export function parse(text: string) {
	if (!text) return DateTime.now()

	let time
	let mathString = ''
	let index
	let parseString

	if (text.startsWith('now')) {
		time = DateTime.now()
		mathString = text.substring(3)
	} else {
		index = text.indexOf('||')
		if (index === -1) {
			parseString = text
		} else {
			parseString = text.substring(0, index)
			mathString = text.substring(index + 2)
		}
		// We're going to just require ISO8601 timestamps, k?
		time = DateTime.fromISO(parseString)
	}

	if (!mathString.length) {
		return time
	}

	return parseDateMath(mathString, time)
}

function parseDateMath(mathString: string, time: DateTime) {
	const len = mathString.length
	let dateTime = time
	let i = 0

	while (i < len) {
		const c = mathString.charAt(i++)
		let num

		// @ts-expect-error incorrect types
		if (isNaN(mathString.charAt(i))) {
			num = 1
		} else {
			const numFrom = i
			// @ts-expect-error incorrect types
			while (!isNaN(mathString.charAt(i))) {
				i++
			}
			num = parseInt(mathString.substring(numFrom, i), 10)
		}

		const unit = units.get(mathString.charAt(i++))

		if (!unit) {
			return dateTime
		}
		if (c === '/') {
			dateTime = dateTime.startOf(unit)
		} else if (c === '+') {
			dateTime = dateTime.plus({ [unit]: num })
		} else if (c === '-') {
			dateTime = dateTime.minus({ [unit]: num })
		}
	}

	return dateTime
}

export function getDateMath(mathString: string) {
	const len = mathString.length
	let i = 3
	const delta: ParsedDateMath = {}

	while (i < len) {
		const c = mathString.charAt(i++)
		let num

		// @ts-expect-error incorrect types
		if (isNaN(mathString.charAt(i))) {
			num = 1
		} else {
			const numFrom = i
			// @ts-expect-error incorrect types
			while (!isNaN(mathString.charAt(i))) {
				i++
			}
			num = parseInt(mathString.substring(numFrom, i), 10)
		}

		const unit = units.get(mathString.charAt(i++))

		if (!unit) {
			return delta
		}
		delta[unit] ??= 0
		if (c === '+') {
			delta[unit] += num
		} else if (c === '-') {
			delta[unit] -= num
		}
	}

	return delta
}

export function diffDateMaths(deltaLeft: ParsedDateMath, deltaRight: ParsedDateMath) {
	const diff: ParsedDateMath = {}

	for (const unit of units.values()) {
		diff[unit] = (deltaRight[unit] ?? 0) - (deltaLeft[unit] ?? 0)
	}

	return diff
}

export function addDateMaths(deltaLeft: ParsedDateMath, deltaRight: ParsedDateMath) {
	const diff: ParsedDateMath = {}

	for (const unit of units.values()) {
		diff[unit] = (deltaRight[unit] ?? 0) + (deltaLeft[unit] ?? 0)
	}

	return diff
}

export function dateMathToString(math: ParsedDateMath): string {
	let string = ''
	for (const [unit, value] of Object.entries(math)) {
		if (!value) continue
		const key = unitsReversed.get(unit as DateTimeUnit)
		if (!key) continue
		string += value > 0 ? '+' : ''
		string += value + key
	}
	return string
}
